// ============================================================================
// /map — full-screen facility map (WS12).
//
// Consumer #2 of the shared <FacilityMap> primitive, opened centred on the
// user's location (Ben's decision: no radius filter — the map opens where you
// are and pans freely).
//
// CW-A Phase 3 (2026-08-04): this page used to read useMapData() unscoped via
// <FacilityMap> with no facilityIds prop — ALWAYS all 231 facilities — while
// the Accounts map tab was fed the list's current filter and was ALWAYS
// scoped. Two surfaces, two different answers to "which facilities". Both now
// consume useFacilityScope, so the answer is the same and the control looks
// the same on both.
// ============================================================================

import { useEffect, useState } from 'react';
import { FacilityMap } from '../../shared/map';
import { supabase } from '../../shared/supabase';
import { useFacilityScope, FacilityScopeControl } from '../../shared/scope';

export default function FacilityMapPage() {
  const { scope, setScope, myFacilityIds, canScopeToMine, resolved } =
    useFacilityScope();
  const [allCount, setAllCount] = useState<number | null>(null);

  // Org-wide total for the control's "All facilities (N)" label. The map's own
  // data fetch (v_map_facilities) only carries GEOCODED rows, so it is not the
  // right denominator for a scope label — count the facilities themselves.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { count, error } = await supabase
        .from('active_facilities')
        .select('facility_id', { count: 'exact', head: true });
      if (cancelled) return;
      setAllCount(error ? null : (count ?? null));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hold the map back until the assigned set has settled, so an AM does not
  // watch all 231 pins draw and then vanish down to their own 30.
  const scopedIds = scope === 'mine' ? myFacilityIds : undefined;

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col px-4 py-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Facility map</h1>
          <p className="text-xs text-tcs-text-muted">
            {scope === 'mine'
              ? 'Your facilities, coloured by whatever you’re asking about.'
              : 'Every facility, coloured by whatever you’re asking about.'}
          </p>
        </div>
        <FacilityScopeControl
          scope={scope}
          onChange={setScope}
          canScopeToMine={canScopeToMine}
          mineCount={myFacilityIds.length}
          allCount={allCount}
        />
      </div>
      {resolved ? (
        // CW-I Phase 3a. This page has passed a height since WS12 and the map
        // stayed pinned at 512px anyway — Shell's own h-[32rem] tied on
        // specificity and won on stylesheet order. With the pin gone the
        // height finally applies. dvh, not vh: vh excludes mobile browser
        // chrome and clipped the bottom of the map.
        <FacilityMap
          facilityIds={scopedIds}
          initialCenter="user"
          className="h-[calc(100dvh-11rem)] flex-1"
        />
      ) : (
        <div
          className="h-[calc(100dvh-11rem)] flex-1 animate-pulse rounded-lg bg-tcs-bg-muted"
          aria-label="Loading map"
        />
      )}
    </div>
  );
}
